import { SizeType } from '@payflip/types/product-builder';
import {
  Button,
  Heading,
  Icon,
  IconName,
  LinkButton,
  Paragraph
} from '@payflip/ui/v2';
import { isLeft, isRight } from 'fp-ts/lib/Either';
import { ProductField, ProductFieldType } from '../../generated/types';
import { message } from '../../utils/message';
import { resolveChoiceField } from '../../utils/resolve-choice-field';
import { Error, Field } from './styled';

export interface ChoiceFieldProps extends Omit<ProductField, '__typename'> {
  noLabel?: boolean;
  noActionText?: boolean;
  context: Record<string, string>;
  config: Record<string, string>;
  form: Record<string, string>;
  download?: (id: string) => Promise<void>;
}

export function ChoiceField({
  label,
  path,
  fallback,
  type,
  noLabel = false,
  noActionText = false,
  context,
  config,
  form,
  download
}: ChoiceFieldProps): JSX.Element {
  const value = resolveChoiceField(path, fallback, type, context, config, form);

  const isAction =
    type === ProductFieldType.Url || type === ProductFieldType.File;

  if (
    type === ProductFieldType.Url &&
    isRight(value) &&
    value.right !== fallback
  ) {
    return (
      <LinkButton
        isExternal
        href={value.right}
        leftIcon={<Icon name={IconName.Link} w="16px" />}
        variant="secondary"
        size={SizeType.Medium}
        children={noActionText === false ? label : undefined}
        w="full"
        h="auto"
        wordBreak="break-word"
        whiteSpace="normal"
      />
    );
  }

  if (type === ProductFieldType.File && isRight(value)) {
    return (
      <Button
        type="button"
        leftIcon={<Icon name={IconName.FileDownload} w="16px" />}
        variant="secondary"
        size="medium"
        onClick={(e): void => {
          e.stopPropagation();

          if (typeof download === 'function') {
            download(value.right);
          }
        }}
        children={noActionText === false ? label : undefined}
      />
    );
  }

  return (
    <Field>
      {!isAction && noLabel === false && (
        <Heading variant="h6">{label}</Heading>
      )}
      {isLeft(value) && <Error>{message(null, 'Missing value')}</Error>}
      {!isAction && isRight(value) && (
        <Paragraph variant="medium">{value.right}</Paragraph>
      )}
    </Field>
  );
}
